import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../api';
import { useOidcContext } from '../contexts/OidcContext';
import { useTranslation } from '../i18n/useTranslation';
import { PendingMemberOut } from '../types/apiRequests';

interface Props {
  /** Fired after approval — SeatsPage refreshes its list since approval grants KC membership. */
  onApproved?: () => void;
}

export default function PendingMembersSection({ onApproved }: Props) {
  const { getAccessTokenSilently } = useOidcContext();
  const t = useTranslation();
  const [pending, setPending] = useState<PendingMemberOut[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      setPending(await api.listPendingMembers(token));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    }
  }, [getAccessTokenSilently, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDecide = async (id: string, decision: 'approve' | 'reject') => {
    setBusyId(id);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.decidePendingMember(token, id, decision);
      setPending((prev) => prev.filter((p) => p.id !== id));
      if (decision === 'approve' && onApproved) {
        onApproved();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card
      sx={{
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
        mb: 3,
        p: 3,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em', mb: 1.5 }}>
        {t('pendingMembers.section.title')}
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {pending.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
          {t('pendingMembers.section.empty')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {pending.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                borderTop: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }} noWrap>
                    {row.email}
                  </Typography>
                  {row.source === 'kc_auto_member' && (
                    <Chip
                      label={t('pendingMembers.source.kcAuto')}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
                {row.name && (
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }} noWrap>
                    {row.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  disabled={busyId === row.id}
                  onClick={() => handleDecide(row.id, 'approve')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('pendingMembers.approve')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  disabled={busyId === row.id}
                  onClick={() => handleDecide(row.id, 'reject')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('pendingMembers.reject')}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
}
